import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin,
  Target,
  TrendingUp,
  UserPlus
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import prospectMasterService from '../../services/prospectMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
import { formatRupeeAmount, formatINR } from '../../utils/currencyFormat';
// Consolidated CSS is imported globally

const ProspectMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProspects: 0,
    activeProspects: 0,
    inactiveProspects: 0,
    stageStats: [],
    categoryStats: []
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [prospectsResponse, statsResponse] = await Promise.all([
        prospectMasterService.getAllProspects(),
        prospectMasterService.getProspectStats()
      ])

      if (prospectsResponse.success) {
        setData(prospectsResponse.data.prospects || [])
        
        if (prospectsResponse.data.prospects.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No prospects found. Create your first prospect to get started.')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(prospectsResponse.message || 'Failed to load prospects')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load prospect data')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    navigate('/crm/prospect-master/new')
  }

  const handleEdit = (row) => {
    navigate('/crm/prospect-master/edit', { 
      state: { prospectData: row } 
    })
  }

  const handleView = (row) => {
    navigate('/crm/prospect-master/view', { 
      state: { prospectData: row } 
    })
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await prospectMasterService.deleteProspect(deleteItem._id)
      
      if (response.success) {
        toast.success('Prospect deleted successfully')
        loadData() // Reload data
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete prospect')
      }
    } catch (error) {
      console.error('Error deleting prospect:', error)
      toast.error('Failed to delete prospect')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Please select prospects to delete')
      return
    }

    try {
      const deletePromises = selectedRows.map(row => 
        prospectMasterService.deleteProspect(row._id)
      )
      
      await Promise.all(deletePromises)
      toast.success(`${selectedRows.length} prospects deleted successfully`)
      loadData() // Reload data
      setSelectedRows([])
    } catch (error) {
      console.error('Error deleting prospects:', error)
      toast.error('Failed to delete selected prospects')
    }
  }


  // Define columns for the data table
  const columns = [
    {
      key: 'prospectCode',
      label: 'Prospect Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <UserPlus size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'prospectName',
      label: 'Prospect Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.prospectNickName && (
            <div className="text-sm text-gray-500">{row.prospectNickName}</div>
          )}
        </div>
      )
    },
    {
      key: 'prospectCategory',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'prospectStage',
      label: 'Stage',
      sortable: true,
      render: (value) => {
        const stageColors = {
          'Lead': 'bg-gray-100 text-gray-800',
          'Qualified': 'bg-yellow-100 text-yellow-800',
          'Proposal': 'bg-blue-100 text-blue-800',
          'Negotiation': 'bg-orange-100 text-orange-800',
          'Closed Won': 'bg-green-100 text-green-800',
          'Closed Lost': 'bg-red-100 text-red-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stageColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'region',
      label: 'Region',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <MapPin size={14} className="text-gray-400" />
          <span>{value}</span>
        </div>
      )
    },
    {
      key: 'expectedDealValue',
      label: 'Deal Value',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <DollarSign size={14} className="text-gray-400" />
          <span>{value ? formatRupeeAmount(value) : 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'leadScore',
      label: 'Lead Score',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    {
      key: 'isProspectActive',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'A' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value === 'A' ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ]

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Masters',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Prospect Master',
      path: '/crm/prospect-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Prospect Master</h1>
          <p>Manage prospect information and track lead progression</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Add Prospect
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalProspects}</div>
          <div className="metric-title">Total Prospects</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeProspects}</div>
          <div className="metric-title">Active Prospects</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Building size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.categoryStats.length}</div>
          <div className="metric-title">Categories</div>
          <div className="metric-subtitle">Prospect types</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.stageStats.length}</div>
          <div className="metric-title">Pipeline Stages</div>
          <div className="metric-subtitle">Lead progression</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search prospects..."
          emptyMessage="No prospects found. Create your first prospect to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Prospect"
        message={`Are you sure you want to delete prospect "${deleteItem?.prospectName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProspectMaster
